import { Medication } from "@types-runway/condition";

export const bottlePricedMedicationKey = "arakoda";

interface MalariaPricingResult {
  price: number;
  quantity: number;
}

/**
 * Calculates malaria medication pricing based on travel duration and medication type.
 *
 * Doxycycline / Atovaquone: $25 per additional week after the first week.
 * Arakoda: $129 per bottle of 8 tablets. First bottle covers ≤ 6 days,
 * each additional bottle covers ~28 more days.
 *
 * See: docs/business-rules/malaria-pricing.md
 */
export const calculateMalariaPricing = (
  medicationName: string,
  travelDays: number,
  basePrice: number,
  baseQuantity: number
): MalariaPricingResult => {
  if (!medicationName) {
    return { price: basePrice, quantity: baseQuantity };
  }

  const isBottlePriced = medicationName.toLowerCase().split(" ")[0] === bottlePricedMedicationKey;

  if (isBottlePriced) {
    const bottlePriceCents = 12900;
    const tabletsPerBottle = 8;
    const firstBottleThreshold = 6;
    const additionalBottleInterval = 28;

    const additionalDays = Math.max(0, travelDays - firstBottleThreshold);
    const bottles = 1 + Math.ceil(additionalDays / additionalBottleInterval);
    return {
      price: bottles * bottlePriceCents,
      quantity: bottles * tabletsPerBottle,
    };
  }

  if (travelDays > 7) {
    const weekIncrease = Math.ceil(travelDays / 7) - 1;
    const priceIncrease = weekIncrease * 2500;
    const quantityIncrease = 7 * weekIncrease;
    return {
      price: basePrice + Math.round(priceIncrease),
      quantity: baseQuantity + Math.round(quantityIncrease),
    };
  }

  return { price: basePrice, quantity: baseQuantity };
};

/**
 * Resolves the correct base quantity for a medication by looking up the selected medication
 * in the available medications list. Falls back to the suggested medication's quantity,
 * then to 0 if neither is available.
 *
 * This is needed because condition.suggestedMedication may differ from the user's
 * actual selection — e.g., backend preloads Arakoda (8 tablets) but user selects
 * Atovaquone (16 tablets), and the pricing calculation must use the correct base.
 */
export const resolveMedicationQuantity = (
  medications: Medication[],
  selectedMedicationName: string | undefined,
  suggestedMedicationQuantity: number | undefined
): number => {
  const selectedMedication = selectedMedicationName
    ? medications.find((med) => med.name === selectedMedicationName)
    : undefined;
  if (selectedMedication) {
    return selectedMedication.quantity;
  }
  return suggestedMedicationQuantity ?? 0;
};
